import { ObjectId, ObjectIdSchema } from '@racemap/sdk/schema/base';
import type { TrackerTag, TrackerTagPrototype } from '@racemap/sdk/schema/trackerTags';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { isObjectChanged } from '@racemap/utilities/functions/isObjectChanged';
import { generateHexColorCode } from '@racemap/utilities/functions/random';
import { castDraft, produce } from 'immer';
import { Tristate } from 'src/components/utils/tristateTask';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { Filters } from '../../../components/TrackerManager/Filter/FilterTreeFunctions';
import type { DraftState, State } from '../../reducers';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

type TagActions = Pick<
  TrackersActions,
  | 'addTrackerTag'
  | 'updateOrCreateTrackerTag'
  | 'removeTrackerTag'
  | 'leaveTrackerTag'
  | 'setTrackerTags'
  | 'updateTrackerTag'
  | 'addTrackersToTag'
  | 'removeTrackersFromTag'
>;

let tagChangesStarted = 0;
let tagChangesFinished = 0;

/** A reload is stale when a tag change started during it, or one is still running. */
export const tagChangeMarker = () => tagChangesStarted;
export const isTagReloadStale = (marker: number) =>
  tagChangesStarted !== marker || tagChangesFinished !== tagChangesStarted;

let settleWaiters: Array<() => void> = [];

/** Resolves once no tag change is running. */
export const whenTagChangesSettle = () =>
  tagChangesFinished === tagChangesStarted
    ? Promise.resolve()
    : new Promise<void>((resolve) => settleWaiters.push(resolve));

const withTagChange = async <T>(change: () => Promise<T>): Promise<T> => {
  tagChangesStarted++;
  try {
    return await change();
  } finally {
    tagChangesFinished++;
    if (tagChangesFinished === tagChangesStarted) {
      for (const resolve of settleWaiters) resolve();
      settleWaiters = [];
    }
  }
};

export const createTagActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): TagActions => {
  const actions = createUntrackedTagActions(set, get);
  return {
    ...actions,
    addTrackerTag: (...args) => withTagChange(() => actions.addTrackerTag(...args)),
    updateTrackerTag: (...args) => withTagChange(() => actions.updateTrackerTag(...args)),
    removeTrackerTag: (...args) => withTagChange(() => actions.removeTrackerTag(...args)),
    leaveTrackerTag: (...args) => withTagChange(() => actions.leaveTrackerTag(...args)),
    addTrackersToTag: (...args) => withTagChange(() => actions.addTrackersToTag(...args)),
    removeTrackersFromTag: (...args) => withTagChange(() => actions.removeTrackersFromTag(...args)),
  };
};

const createUntrackedTagActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): TagActions => ({
  addTrackerTag: async (trackerIds, tagLabel) => {
    const tempObjectId = new ObjectId();
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      const tag: TrackerTagPrototype = {
        trackerIds,
        label: tagLabel,
        color: generateHexColorCode(),
      };

      const creatorId = get().users.currentUserId;
      set(
        produce((s: DraftState) => {
          s.trackers.trackerTags.set(tempObjectId.toHexString(), {
            ...tag,
            id: tempObjectId,
            creatorId: creatorId != null ? ObjectIdSchema.parse(creatorId) : new ObjectId(),
            editorIds: [],
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(tempObjectId.toHexString(), trackerIds.length);
        }),
      );

      const createdTrackerTag = await apiClient.createTrackerTag(tag);

      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
          s.trackers.trackerTags.delete(tempObjectId.toHexString());
          s.trackers.trackerTags.set(createdTrackerTag.id.toHexString(), createdTrackerTag);
          s.trackers.filterCounts.get(Filters.Tags)?.delete(tempObjectId.toHexString());
          s.trackers.filterCounts
            .get(Filters.Tags)
            ?.set(createdTrackerTag.id.toHexString(), trackerIds.length);
        }),
      );

      toast.success('Tracker tag added successfully.');
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          s.trackers.trackerTags.delete(tempObjectId.toHexString());
          s.trackers.filterCounts.get(Filters.Tags)?.delete(tempObjectId.toHexString());
        }),
      );

      if (error instanceof HTTPFetchError) {
        console.log(`Failed to add tracker tag: ${error.serverError}`);
        toast.error(
          `Failed to add tracker tag: ${
            error.serverError?.startsWith('MongoError: E11000')
              ? `Tag label "${tagLabel}" can only be used once.`
              : error.serverError
          }`,
        );
      } else if (error instanceof Error) {
        console.log(`Failed to add tracker tag: ${error.message}`);
        toast.error('Failed to add tracker tag.');
      }
    }
  },
  updateOrCreateTrackerTag: async (selectedTag, trackers) => {
    if (!selectedTag || !trackers) return;

    const existingTag = get().trackers.trackerTags.get(selectedTag);
    const trackerIds = trackers.map((tracker) => tracker.id);

    if (!existingTag) {
      get().trackers.actions.addTrackerTag(trackerIds, selectedTag);
    } else {
      get().trackers.actions.addTrackersToTag(existingTag.id, trackerIds);
    }
  },
  setTrackerTags: (tags) =>
    set(
      produce((s: DraftState) => {
        s.trackers.trackerTags = new Map(tags.map((tag) => [tag.id.toHexString(), castDraft(tag)]));
        s.trackers.filterCounts.set(Filters.Tags, new Map());
        for (const tag of tags) setVisibleCount(s, tag.id.toHexString());
      }),
    ),
  removeTrackerTag: (tagId) =>
    dropTag(set, get, tagId, () => apiClient.deleteTrackerTag(tagId), {
      success: 'Removed tracker tag.',
      failure: 'Failed to remove tracker tag.',
    }),
  leaveTrackerTag: (tagId) =>
    dropTag(set, get, tagId, () => apiClient.leaveTrackerTag(tagId), {
      success: 'You left the tracker tag.',
      failure: 'Failed to leave tracker tag.',
    }),

  updateTrackerTag: async (tagId, trackerTagUpdate) => {
    const oldTrackerTag = get().trackers.trackerTags.get(tagId.toHexString());
    if (oldTrackerTag == null) return;

    const newTrackerTag = {
      ...oldTrackerTag,
      ...trackerTagUpdate,
    };

    if (!isObjectChanged(oldTrackerTag, newTrackerTag)) {
      toast.info('Nothing to update.');
      return;
    }

    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
          s.trackers.trackerTags.set(tagId.toHexString(), castDraft(newTrackerTag));
        }),
      );
      const updatedTrackerTag = await apiClient.updateTrackerTag(tagId, trackerTagUpdate);

      toast.success('Tracker tag updated successfully.');
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
          s.trackers.trackerTags.set(tagId.toHexString(), updatedTrackerTag);
        }),
      );
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          s.trackers.trackerTags.set(tagId.toHexString(), castDraft(oldTrackerTag));
        }),
      );
      if (error instanceof Error) {
        console.log(`Failed to update tracker tag: ${error.message}`);
        toast.error('Failed to update tracker tag.');
      }
    }
  },

  addTrackersToTag: (tagId, trackerIds) =>
    saveTagTrackers(set, get, tagId, { add: trackerIds.map(String) }, () =>
      apiClient.addTrackersToTag(tagId, trackerIds),
    ),
  removeTrackersFromTag: (tagId, trackerIds) =>
    saveTagTrackers(set, get, tagId, { remove: trackerIds.map(String) }, () =>
      apiClient.removeTrackersFromTag(tagId, trackerIds),
    ),
});

/** Removes a tag from the list right away and puts it back if the request fails. */
const dropTag = async (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
  tagId: ObjectId,
  request: () => Promise<unknown>,
  messages: { success: string; failure: string },
) => {
  const key = tagId.toHexString();
  const tag = get().trackers.trackerTags.get(key);
  const count = get().trackers.filterCounts.get(Filters.Tags)?.get(key);
  try {
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.SPINNING;
        s.trackers.trackerTags.delete(key);
        s.trackers.filterCounts.get(Filters.Tags)?.delete(key);
      }),
    );
    await request();
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.NORMAL;
      }),
    );
    toast.success(messages.success);
  } catch (error) {
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = Tristate.FAILURE;
        if (tag != null) s.trackers.trackerTags.set(key, castDraft(tag));
        if (count != null) s.trackers.filterCounts.get(Filters.Tags)?.set(key, count);
      }),
    );
    if (error instanceof Error) {
      console.log(`${messages.failure} ${error.message}`);
      toast.error(messages.failure);
    }
  }
};

type TrackersChange = { add?: Array<string>; remove?: Array<string> };

const applyTrackersChange = (s: DraftState, tagId: string, change: TrackersChange) => {
  const tag = s.trackers.trackerTags.get(tagId);
  if (tag == null) return;
  const ids = new Set(tag.trackerIds.map((id) => id.toHexString()));
  for (const id of change.remove ?? []) ids.delete(id);
  for (const id of change.add ?? []) ids.add(id);
  tag.trackerIds = Array.from(ids, (id) => new ObjectId(id));
  setVisibleCount(s, tagId);
};

/** Recounts every tag after trackers left or came back to the list. */
export const refreshTagCounts = (s: DraftState) => {
  for (const tagId of s.trackers.trackerTags.keys()) setVisibleCount(s, tagId);
};

/** The sidebar counts only the trackers of a tag the viewer can see. */
const setVisibleCount = (s: DraftState, tagId: string) => {
  const tag = s.trackers.trackerTags.get(tagId);
  if (tag == null) return;
  const visible = tag.trackerIds.filter((id) => s.trackers.items.has(id.toHexString())).length;
  s.trackers.filterCounts.get(Filters.Tags)?.set(tagId, visible);
};

// ponytail: one queue per tag so a late response cannot overwrite a newer one; entries are never pruned
const tagQueues = new Map<string, Promise<void>>();
// changes sent but not answered yet, in queue order; each answer is rebased onto the rest
const pendingChanges = new Map<string, Array<TrackersChange>>();
// the trackers the server last confirmed; the tag shown is this plus the pending changes
const confirmedTrackerIds = new Map<string, Array<ObjectId>>();

const saveTagTrackers = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
  tagId: ObjectId,
  change: TrackersChange,
  request: () => Promise<TrackerTag>,
) => {
  const key = tagId.toHexString();
  const pending = pendingChanges.get(key) ?? [];
  if (pending.length === 0) {
    confirmedTrackerIds.set(key, [...(get().trackers.trackerTags.get(key)?.trackerIds ?? [])]);
  }
  pending.push(change);
  pendingChanges.set(key, pending);

  set(
    produce((s: DraftState) => {
      s.trackers.savingState = Tristate.SPINNING;
      applyTrackersChange(s, key, change);
    }),
  );

  const next = (tagQueues.get(key) ?? Promise.resolve()).then(() =>
    runTagTrackersRequest(set, key, request, pending),
  );
  tagQueues.set(key, next);
  return next;
};

/** Shows the confirmed trackers with the changes still waiting for an answer on top. */
const rebuildTagTrackers = (s: DraftState, tagId: string, pending: Array<TrackersChange>) => {
  const tag = s.trackers.trackerTags.get(tagId);
  if (tag != null) tag.trackerIds = [...(confirmedTrackerIds.get(tagId) ?? tag.trackerIds)];
  for (const change of pending) applyTrackersChange(s, tagId, change);
  setVisibleCount(s, tagId);
};

const runTagTrackersRequest = async (
  set: StoreApi<State>['setState'],
  tagId: string,
  request: () => Promise<TrackerTag>,
  pending: Array<TrackersChange>,
) => {
  try {
    const updatedTag = await request();
    pending.shift();
    confirmedTrackerIds.set(tagId, updatedTag.trackerIds);

    toast.success('Tracker tag updated successfully.');
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = pending.length === 0 ? Tristate.NORMAL : Tristate.SPINNING;
        s.trackers.trackerTags.set(tagId, castDraft(updatedTag));
        rebuildTagTrackers(s, tagId, pending);
      }),
    );
  } catch (error) {
    pending.shift();
    set(
      produce((s: DraftState) => {
        s.trackers.savingState = pending.length === 0 ? Tristate.FAILURE : Tristate.SPINNING;
        rebuildTagTrackers(s, tagId, pending);
      }),
    );
    if (error instanceof Error) {
      console.log(`Failed to update tracker tag: ${error.message}`);
      toast.error('Failed to update tracker tag.');
    }
  }
};
